/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro';
import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { StylingContextState } from 'context/Styling';
import { useStyles } from 'utils/useStyles';

const getStyles = ({ colors, viewport: { md } }: StylingContextState) => ({
  backdrop: css`
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
  `,
  modal: css`
    background-color: ${colors.white};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin: ${md ? '0' : '1rem'};
    max-width: ${md ? '500px' : 'calc(100% - 2rem)'};
    padding: ${md ? '2rem' : '1.5rem'};
    width: 100%;
  `,
});

export interface ModalProps {
  isOpen: boolean;
  children: ReactNode;
  onClose?: () => void;
  ariaLabel?: string;
}

export const Modal = ({ isOpen, children, onClose, ariaLabel = 'Modal' }: ModalProps) => {
  const styles = useStyles(getStyles);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return createPortal(
    <div css={styles.backdrop} onClick={handleBackdropClick}>
      <div css={styles.modal} role="dialog" aria-modal="true" aria-label={ariaLabel}>
        {children}
      </div>
    </div>,
    document.body
  );
};
